import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Footer from "../components/Footer";
import PublicNav from "../components/PublicNav";
import { getPostBySlug } from "../data/posts";

// Vite-specific: glob-import all .md files at build time as raw text.
// `eager: true` resolves them synchronously so we don't need an async
// loader; `query: "?raw"` returns the file contents as a string.
const postModules = import.meta.glob("../content/posts/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const meta = getPostBySlug(slug);
  const [content, setContent] = useState("");

  useEffect(() => {
    if (!meta) return;
    // Look up the markdown file content from the glob-imported modules.
    // Key shape: "../content/posts/<slug>.md"
    const key = `../content/posts/${slug}.md`;
    const raw = postModules[key];
    setContent(raw || "");
  }, [slug, meta]);

  // Unknown slug → redirect to blog index. No need for a custom 404 for now.
  if (!meta) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav />

      <main className="flex-1 max-w-3xl mx-auto px-6 py-16 w-full">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-block mb-8 text-sm text-brand-violet dark:text-brand-violet-on-dark hover:opacity-80 transition"
        >
          ← All posts
        </Link>

        {/* Post header */}
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-3 text-xs text-gray-400 uppercase tracking-widest">
            <time>{formatDate(meta.date)}</time>
            <span>·</span>
            <span>{meta.readingTimeMinutes} min read</span>
          </div>
          <h1 className="text-4xl font-extrabold leading-tight">
            {meta.title}
          </h1>
        </header>

        {/* Post content */}
        <article
          className="prose prose-lg dark:prose-invert max-w-none
                            prose-headings:font-bold
                            prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                            prose-p:text-gray-600 dark:prose-p:text-gray-300
                            prose-p:leading-relaxed
                            prose-a:text-brand-violet dark:prose-a:text-brand-violet-on-dark prose-a:no-underline
                            hover:prose-a:opacity-80
                            prose-strong:text-gray-900 dark:prose-strong:text-white
                            prose-code:text-brand-violet dark:prose-code:text-brand-violet-on-dark
                            prose-blockquote:border-l-brand-violet
                            prose-blockquote:text-gray-500
                            dark:prose-blockquote:text-gray-400"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </article>

        {/* Footer CTA */}
        <div className="mt-16 pt-8 border-t border-gray-100 dark:border-white/10">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Joborigo is recruitment transparency software for agencies,
            employers, and job seekers.
          </p>
          <Link
            to="/register"
            className="inline-block bg-brand-violet text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
          >
            Get started for free
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
